import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { ExamRunner } from "@/components/pathway/ExamRunner";
import { ExamReport } from "@/components/pathway/ExamReport";

export default function GedExam() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [x, setX] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let t;
    const load = () => http.get(`/pathway/exam/${examId}`).then((r) => { setX(r.data); if (r.data.status === "generating") t = setTimeout(load, 4000); }).catch((e) => setErr(formatApiError(e)));
    load();
    return () => clearTimeout(t);
  }, [examId]);

  const start = async () => { setBusy(true); try { setX((await http.post(`/pathway/exam/${examId}/start`)).data); } catch (e) { toast.error(formatApiError(e)); } setBusy(false); };
  const submit = async (body) => {
    setBusy(true);
    try { setX((await http.post(`/pathway/exam/${examId}/submit`, body)).data); localStorage.removeItem(`cl_exam_${examId}`); window.scrollTo({ top: 0 }); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const discard = async () => { await http.delete(`/pathway/exam/${examId}`); nav(`/pathway/ged/${x.subject_id}`); };

  if (err) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-600" data-testid="exam-error">{err}</div>;
  if (!x) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 pb-24" data-testid="ged-exam-page" data-status={x.status}>
      <Link to={`/pathway/ged/${x.subject_id}`} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="exam-back-link"><ArrowLeft className="w-4 h-4" /> {x.subject}</Link>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-4 mb-8">Full-length GED Practice Exam: {x.subject}</h1>
      {x.status === "generating" && <div className="rounded-3xl bg-[#F7F3EB] p-10 text-center" data-testid="exam-generating"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#6B9080]" /><p className="font-display text-xl text-[#2C4A3E] mt-4">Building your {x.total}-question exam…</p><p className="text-sm text-stone-500 mt-1">This takes about 1-2 minutes. Take a few slow breaths while you wait.</p>
        <button onClick={discard} data-testid="exam-generating-cancel-btn" className="mt-4 text-sm underline text-stone-500">Cancel</button></div>}
      {x.status === "failed" && <div className="rounded-3xl bg-[#FBEDE8] p-8" data-testid="exam-failed"><p>Something went wrong building this exam.</p><button onClick={discard} className="mt-3 underline text-[#C87D53]">Go back and try again</button></div>}
      {x.status === "ready" && (
        <div className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-8 max-w-2xl" data-testid="exam-intro">
          <p className="inline-flex items-center gap-2 text-[#2C4A3E] font-display text-2xl"><Timer className="w-6 h-6 text-[#C87D53]" /> {x.questions.length} questions{x.essay && " + essay"} · {x.duration_min} minutes</p>
          <ul className="mt-4 text-sm text-stone-600 space-y-1.5 list-disc pl-5"><li>The timer starts when you press Start and keeps running if you leave the page.</li><li>Flag any question to come back to it.</li><li>The exam submits automatically when time runs out.</li><li>Find a quiet place, sit tall, and take three slow breaths first.</li></ul>
          <div className="flex gap-3 mt-6"><button onClick={start} disabled={busy} data-testid="exam-start-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white disabled:opacity-60">Start exam</button>
            <button onClick={discard} data-testid="exam-discard-btn" className="px-6 py-3 rounded-full border border-[#E6DEC3] text-sm">Not now</button></div>
        </div>
      )}
      {x.status === "in_progress" && <ExamRunner exam={x} onSubmit={submit} busy={busy} />}
      {x.status === "submitted" && <ExamReport exam={x} unitIndex={(u) => Math.max(0, x.units.indexOf(u))} />}
    </div>
  );
}
